import { Box, IconButton, Typography } from "@mui/material";
import { memo } from "react";
import {
  smallIconStyle,
  QUOTED_CHIP_CONTAINER_SX,
  QUOTED_CHIP_ICON_SX,
  QUOTED_CHIP_TEXT_SX,
  QUOTED_CHIP_CLOSE_SX,
  QUOTED_CHIP_CLOSE_ICON_SX,
  HOVER_LABEL_SX,
} from "./ChatBot.styles";
import DoubleQuotesIcon from "../../../assets/images/dbquotes.svg";
import CloseIcon from "@mui/icons-material/Close";

export const OUTPUT_OPTIONS = [
  { label: "Table", value: "table" },
  // { label: "Graph", value: "graph" },
  { label: "Only Text", value: "only" },
];
export const INTRO_REVEAL_DELAYS = [1000, 2000, 3000, 4000];
export const QUICK_OPTIONS = [
  { label: "Summarize", value: "summarize" },
  { label: "Rewrite", value: "rewrite" },
  { label: "Translate", value: "translate" },
  { label: "Explain", value: "explain" },
];
export const CATEGORY_OPTIONS = [
  "Sales",
  "Financial Insights",
  "Sales Analysis",
  "Marketing Performance",
  "Customer Engagement",
  "Product Development",
  "Visibility Enhancement",
];

// ── Quoted text chip shown above ChatInput after "Ask Samarth" ──
export const QuotedTextChip = memo(({ text, onClose }) => (
  <Box sx={QUOTED_CHIP_CONTAINER_SX}>
    <IconButton size="small" sx={QUOTED_CHIP_ICON_SX}>
      <Box component="img" src={DoubleQuotesIcon} />
    </IconButton>
    <Typography sx={QUOTED_CHIP_TEXT_SX}>{text}</Typography>
    <IconButton size="small" onClick={onClose} sx={QUOTED_CHIP_CLOSE_SX}>
      <CloseIcon sx={QUOTED_CHIP_CLOSE_ICON_SX} />
    </IconButton>
  </Box>
));

// ── Header icon with hover-revealed label (New Chat / Prompts & History) ──
export const HeaderIconWithLabel = memo(
  ({ label, icon, onClick, labelMr = 1, labelClassName }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        position: "relative",
        [`&:hover .${labelClassName}`]: {
          opacity: 1,
          visibility: "visible",
        },
      }}
    >
      <Typography
        className={labelClassName}
        sx={{ ...HOVER_LABEL_SX, mr: labelMr }}
      >
        {label}
      </Typography>
      <Box component="img" src={icon} sx={smallIconStyle} onClick={onClick} />
    </Box>
  ),
);

export const truncateWords = (text, wordLimit = 6) => {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};

// ── Helper: build graph object from API payload ──
export const buildGraphObject = (graphPayload, graph) => {
  if (!graph) return null;

  const raw = graph?.graph_data;

  // 🔥 HANDLE HEATMAP STRUCTURE
  const isHeatmap = raw && !Array.isArray(raw) && Array.isArray(raw?.data);

  // 🔥 HANDLE PIE / SIMPLE CHART
  const isArrayGraph = Array.isArray(raw);

  let data = [];

  if (isHeatmap) {
    data = raw.data; // ✅ heatmap rows
  } else if (isArrayGraph) {
    data = raw; // ✅ pie chart
  }

  return {
    type: graphPayload?.type,
    title: graph?.graph_title,
    variant: graph?.variant,

    xKey: graph?.data_points?.name,
    yKey: graph?.data_points?.value,
    line: graph?.data_points?.line || null,

    data,

    // keep extra safety
    raw,
  };
};

// ── Helper: detect if graph payload contains usable rows ──
export const hasGraphRows = (graph) => {
  const raw = graph?.graph_data;

  if (!raw) return false;

  if (Array.isArray(raw)) return raw.length > 0;

  if (raw?.data && Array.isArray(raw.data)) return raw.data.length > 0;

  return false;
};
// ── Helper: parse quoted prefix + trailing `key=val1,val2;` filters from question ──
export const FILTER_SEGMENT_RE = /\s*([A-Za-z0-9_]+)=([^;]+);/g;
export const QUOTE_PREFIX_RE = /^>\s([\s\S]*?)\n\n/;
export const parseFiltersFromQuestion = (question) => {
  if (!question) return { text: "", filters: {}, quotedText: "" };

  let remaining = question;
  let quotedText = "";
  const quoteMatch = remaining.match(QUOTE_PREFIX_RE);
  if (quoteMatch) {
    quotedText = quoteMatch[1].trim();
    remaining = remaining.slice(quoteMatch[0].length);
  }

  const filters = {};
  let match;
  FILTER_SEGMENT_RE.lastIndex = 0;
  while ((match = FILTER_SEGMENT_RE.exec(remaining)) !== null) {
    const key = match[1].trim();
    const values = match[2]
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (values.length) filters[key] = values;
  }
  const text = remaining.replace(FILTER_SEGMENT_RE, "").trim();
  return { text, filters, quotedText };
};
